import type { Punchout } from "./punchout";

export type BusinessModel = "owned" | "mixed" | "managed";

export type PAS =
  | "AppFolio" | "Buildium" | "Yardi" | "Rent Manager"
  | "Rentvine" | "Propertyware" | "None";

export type Corporate =
  | "QuickBooks Online" | "QuickBooks Desktop"
  | "Xero" | "Sage Intacct"
  | "Bill.com" | "AvidXchange" | "Coupa"
  | "Same as PAS" | "Other";

export type PmsSyncMode =
  | "line-items"
  | "bill-object"
  | "pre-bill"
  | "pdf-bucket"
  | "none";

export type ContractModel =
  | "markup-internal-only"
  | "markup-internal-and-invoices"
  | "pass-through"
  | "flat-rate-per-owner";

export type MarkupKind = "flat" | "percentage" | "other";
export type MarkupBasis = "universal" | "by-owner" | "by-vendor" | "by-category";

export type BillRateMode = "derived" | "independent";

export interface WizardConfig {
  // 1. Business model
  businessModel: BusinessModel | null;

  // 2. Systems
  pas: PAS | null;
  corporate: Corporate | null;
  corporateOther: string;

  // 3. AP / AR routing — checkboxes default unchecked
  apDestPas: boolean;
  apDestCorporate: boolean;
  arDestPas: boolean;
  arDestCorporate: boolean;
  pmsSyncMode: PmsSyncMode | null;

  // 4. Contract model
  contractModel: ContractModel | null;

  // 5. Markup application — two-tier
  markupKind: MarkupKind | null;
  markupBasis: MarkupBasis | null;
  markupOther: string;
  markupValue: number;       // numeric value for universal flat/% — keeps default

  // 6. Shop rate (default kept)
  shopRate: number;
  shopOverrideTech: boolean;
  shopOverrideService: boolean;
  shopRateOverrideNotes: string;

  // 7. Bill rate (default kept)
  billRateMode: BillRateMode | null;
  billRate: number;

  // AP / AR email recipients (inline next to corporate-accounting
  // checkbox in step 3). Always PDF.
  apEmail: string;
  arEmail: string;

  // 9. AR invoice branding — null until selected
  usesCustomArBranding: boolean | null;
  arBrandingNotes: string;

  // 10. Open-ended additional notes
  additionalNotes: string;
}

export interface SubmissionResponse {
  id: string;
  created_at: string;
  iap_email: string;
  punchout: Punchout;
  config: WizardConfig;
}

export interface MeResponse {
  email: string;
  is_admin: boolean;
}

// --- Admin sign-in (Google ID token) ----------------------------------
//
// The survey is publicly accessible — no Google login required to fill
// it. Admin routes verify a Google-issued ID token, obtained via Google
// Identity Services on the /admin page. We stash the token in
// sessionStorage so admin API calls authenticate automatically. ID
// tokens expire (~1 hour); on 401 the admin page prompts to re-sign-in.

const GOOGLE_ID_TOKEN_KEY = "truecost-survey:google-id-token";

export function getGoogleIdToken(): string {
  return sessionStorage.getItem(GOOGLE_ID_TOKEN_KEY) ?? "";
}

export function setGoogleIdToken(token: string): void {
  if (token) sessionStorage.setItem(GOOGLE_ID_TOKEN_KEY, token);
  else sessionStorage.removeItem(GOOGLE_ID_TOKEN_KEY);
}

export interface AppConfig {
  google_client_id: string;
  is_cloud_run: boolean;
}

async function jsonFetch<T>(url: string, init?: RequestInit, options?: { admin?: boolean }): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  if (options?.admin) {
    const token = getGoogleIdToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }
  const r = await fetch(url, { ...init, headers });
  if (!r.ok) {
    const detail = await r.text().catch(() => "");
    throw new Error(`${r.status} ${r.statusText}: ${detail}`);
  }
  return r.json() as Promise<T>;
}

export const api = {
  config: () => jsonFetch<AppConfig>("/api/config"),

  me: () => jsonFetch<MeResponse>("/api/me", undefined, { admin: true }),

  createSubmission: (config: WizardConfig, punchout: Punchout) =>
    jsonFetch<SubmissionResponse>("/api/submissions", {
      method: "POST",
      body: JSON.stringify({ config, punchout }),
    }),

  getSubmission: (id: string) =>
    jsonFetch<SubmissionResponse>(`/api/submissions/${id}`),

  listSubmissions: () =>
    jsonFetch<SubmissionResponse[]>("/api/admin/submissions", undefined, { admin: true }),

  documentUrl: (submissionId: string, kind: "ap" | "ar" | "config") =>
    `/api/submissions/${submissionId}/documents/${kind}`,

  csvUrl: () => {
    // For direct browser link downloads we send the token as a query
    // param — the backend accepts either header or `?token=`.
    const t = getGoogleIdToken();
    return t ? `/api/admin/submissions.csv?token=${encodeURIComponent(t)}` : "/api/admin/submissions.csv";
  },
};

export type DocumentKind = "ap" | "ar" | "config";

export const DOCUMENT_LABEL: Record<DocumentKind, string> = {
  ap: "Sample AP bill",
  ar: "Sample AR invoice",
  config: "Configuration summary",
};
